import React, { useState } from "react";
import { Alert, Box, Button, CircularProgress, Divider, IconButton, InputAdornment, Paper, Stack, TextField, Typography } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import CognizantLogo from "../cognizant-logo.svg";
import authApi, { login, storeSession } from "../services/auth";

const emptySignup = { userId: "", name: "", email: "", password: "", confirmPassword: "" };

const LoginPage = ({ onLogin, sessionMessage }) => {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ username: "", password: "" });
  const [signup, setSignup] = useState(emptySignup);
  const [reset, setReset] = useState({ username: "", email: "", code: "", password: "" });
  const [resetStage, setResetStage] = useState("lookup");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const switchMode = (next) => { setMode(next); setError(""); setSuccess(""); setResetStage("lookup"); };

  const handleLogin = async (event) => {
    event.preventDefault(); setError(""); setLoading(true);
    try {
      await login({ username: form.username.trim(), password: form.password });
      onLogin?.();
      window.location.assign("/home");
    } catch (err) { setError(err.response?.data?.error || "Unable to sign in"); }
    finally { setLoading(false); }
  };

  const handleSignup = async (event) => {
    event.preventDefault(); setError("");
    if (signup.password !== signup.confirmPassword) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      const { data } = await authApi.post("/signup", signup);
      setSuccess(data.message); setSignup(emptySignup);
    } catch (err) { setError(err.response?.data?.error || "Unable to submit your request"); }
    finally { setLoading(false); }
  };

  const handleForgot = async (event) => {
    event.preventDefault(); setError(""); setLoading(true);
    try {
      if (resetStage === "lookup") {
        const { data } = await authApi.post("/forgot-password/lookup", { username: reset.username.trim() });
        setReset((current) => ({ ...current, email: data.pendingEmail || data.email || "" }));
        if (data.pendingEmail) {
          setSuccess("Your recovery email change is waiting for administrator approval.");
          setResetStage("approval");
        } else {
          setResetStage("email");
        }
      } else if (resetStage === "email") {
        const { data } = await authApi.post("/forgot-password", { username: reset.username.trim(), email: reset.email.trim() });
        setSuccess(data.message);
        setResetStage(data.approvalRequired ? "approval" : "code");
      } else if (resetStage === "code") {
        const { data } = await authApi.post("/confirm-forgot-password", reset);
        storeSession(data);
        onLogin?.();
        window.location.assign("/home");
      }
    } catch (err) {
      if (err.response?.data?.passwordChanged) {
        setForm({ username: reset.username.trim(), password: "" });
        setMode("login");
      }
      setError(err.response?.data?.error || "Unable to reset password");
    }
    finally { setLoading(false); }
  };

  const passwordField = (value, onChange, label = "Password") => (
    <TextField label={label} type={showPassword ? "text" : "password"} value={value} onChange={onChange} required fullWidth autoComplete="current-password"
      sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#fbfdff", "&:hover fieldset": { borderColor: "#8bb7d8" }, "&.Mui-focused": { bgcolor: "white", boxShadow: "0 0 0 3px rgba(0,105,180,.08)" } } }}
      InputProps={{ endAdornment: <InputAdornment position="end"><IconButton aria-label={showPassword ? "Hide password" : "Show password"} onClick={() => setShowPassword((shown) => !shown)} edge="end" size="small" sx={{ color: "#57748e", bgcolor: "transparent", "&:hover": { bgcolor: "#edf5fb", color: "#0069b4" } }}>{showPassword ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}</IconButton></InputAdornment> }} />
  );

  return (
    <Box sx={{ minHeight: "100vh", display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(420px, 0.9fr) 1.1fr" }, bgcolor: "#f5f8fc" }}>
      <Box sx={{ display: { xs: "none", md: "flex" }, position: "relative", overflow: "hidden", p: 7, flexDirection: "column", justifyContent: "space-between", color: "white", background: "linear-gradient(145deg, #003b73 0%, #005eb8 52%, #12a8cd 100%)" }}>
        <Box sx={{ position: "absolute", width: 440, height: 440, borderRadius: "50%", border: "1px solid rgba(255,255,255,.2)", right: -130, top: -110 }} />
        <Box sx={{ position: "absolute", width: 260, height: 260, borderRadius: "50%", bgcolor: "rgba(255,255,255,.06)", left: -80, bottom: 70 }} />
        <Box sx={{ zIndex: 1 }}><img src={CognizantLogo} alt="Cognizant" style={{ height: 34, filter: "brightness(0) invert(1)" }} /></Box>
        <Box sx={{ zIndex: 1, maxWidth: 560 }}>
          <Typography variant="overline" sx={{ letterSpacing: 2.2, opacity: 0.82 }}>Workforce intelligence</Typography>
          <Typography variant="h2" sx={{ mt: 1, fontWeight: 400, fontSize: { md: "3.04rem", lg: "3.64rem" }, lineHeight: 1.08 }}>
            <Box component="span" sx={{ fontWeight: 700 }}>Demand</Box>{" "}
            <Box component="span" sx={{ fontSize: "calc(1em - 3pt)", fontWeight: 400 }}>and</Box>{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>Supply</Box>
            <Box component="span" sx={{ fontSize: "calc(1em - 3pt)", fontWeight: 400 }}>, brought into</Box>{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>Focus</Box>.
          </Typography>
          <Typography sx={{ mt: 3, fontSize: "calc(1.05rem - 1px)", lineHeight: 1.7, maxWidth: 500, opacity: 0.86 }}>Secure access to planning insights, supply signals, and the decisions that move your teams forward.</Typography>
        </Box>
        <Typography variant="caption" sx={{ zIndex: 1, opacity: 0.7 }}>EI & PO Demand Supply Management</Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: { xs: 2.5, sm: 5 } }}>
        <Paper elevation={0} sx={{ width: "100%", maxWidth: 480, p: { xs: 3, sm: 5 }, borderRadius: 4, border: "1px solid #e3eaf3", boxShadow: "0 24px 70px rgba(23, 63, 103, .12)" }}>
          <Stack spacing={3}>
            <Box>
              {mode !== "login" && <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => switchMode("login")} sx={{ mb: 2, ml: -1, textTransform: "none" }}>Back to sign in</Button>}
              <Box sx={{ width: 48, height: 48, display: "grid", placeItems: "center", bgcolor: "#e8f2fc", color: "#005eb8", borderRadius: 2.5, mb: 2 }}><LockOutlinedIcon /></Box>
              <Typography variant="h4" sx={{ fontWeight: 750, color: "#12324f" }}>{mode === "login" ? "Welcome back" : mode === "signup" ? "Request access" : "Reset password"}</Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>{mode === "login" ? "Sign in with your employee credentials." : mode === "signup" ? "Your request will be reviewed by an administrator." : "We’ll send a verification code to your registered email."}</Typography>
            </Box>

            {sessionMessage && mode === "login" && <Alert severity="info">{sessionMessage}</Alert>}
            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}

            {mode === "login" && <Box component="form" onSubmit={handleLogin}><Stack spacing={2.25}>
              <TextField label="Employee ID" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required fullWidth autoFocus autoComplete="username" />
              {passwordField(form.password, (e) => setForm({ ...form, password: e.target.value }))}
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}><Button onClick={() => switchMode("forgot")} sx={{ textTransform: "none", p: 0 }}>Forgot password?</Button></Box>
              <Button type="submit" variant="contained" size="large" disabled={loading} sx={{ py: 1.35, textTransform: "none", fontWeight: 700, borderRadius: 2 }}>{loading ? <CircularProgress size={24} color="inherit" /> : "Sign in"}</Button>
              <Divider>New to the application?</Divider>
              <Button variant="outlined" size="large" onClick={() => switchMode("signup")} sx={{ py: 1.2, textTransform: "none", fontWeight: 700, borderRadius: 2 }}>Sign up</Button>
            </Stack></Box>}

            {mode === "signup" && <Box component="form" onSubmit={handleSignup}><Stack spacing={2}>
              <TextField label="Employee ID" value={signup.userId} onChange={(e) => setSignup({ ...signup, userId: e.target.value })} required autoFocus />
              <TextField label="Full name" value={signup.name} onChange={(e) => setSignup({ ...signup, name: e.target.value })} required />
              <TextField label="Work email" type="email" value={signup.email} onChange={(e) => setSignup({ ...signup, email: e.target.value })} required />
              {passwordField(signup.password, (e) => setSignup({ ...signup, password: e.target.value }))}
              <TextField label="Confirm password" type={showPassword ? "text" : "password"} value={signup.confirmPassword} onChange={(e) => setSignup({ ...signup, confirmPassword: e.target.value })} required />
              <Button type="submit" variant="contained" size="large" disabled={loading} sx={{ py: 1.35, textTransform: "none", fontWeight: 700 }}>{loading ? <CircularProgress size={24} color="inherit" /> : "Submit for approval"}</Button>
            </Stack></Box>}

            {mode === "forgot" && <Box component="form" onSubmit={handleForgot}><Stack spacing={2}>
              <TextField label="Employee ID" value={reset.username} onChange={(e) => setReset({ ...reset, username: e.target.value })} required autoFocus disabled={resetStage !== "lookup"} />
              {resetStage !== "lookup" && <TextField label="Recovery email" type="email" value={reset.email} onChange={(e) => setReset({ ...reset, email: e.target.value })} required disabled={resetStage === "code" || resetStage === "approval"} helperText="Use your @cognizant.com address. New or changed addresses require administrator approval." />}
              {resetStage === "code" && <TextField label="Verification code" value={reset.code} onChange={(e) => setReset({ ...reset, code: e.target.value })} required />}
              {resetStage === "code" && passwordField(reset.password, (e) => setReset({ ...reset, password: e.target.value }), "New password")}
              {resetStage === "approval" && <Button onClick={() => { setResetStage("lookup"); setSuccess(""); }}>Check approval status</Button>}
              {resetStage !== "approval" && <Button type="submit" variant="contained" size="large" disabled={loading} sx={{ py: 1.35, textTransform: "none", fontWeight: 700 }}>{loading ? <CircularProgress size={24} color="inherit" /> : resetStage === "lookup" ? "Find account" : resetStage === "email" ? "Send reset code" : "Set new password"}</Button>}
            </Stack></Box>}
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
};

export default LoginPage;
